namespace Portico.Services {

	import Inject = Portico.Decorators.Inject;
	import Service = Portico.Decorators.Service;

	import pm = Portico.Models;
	import pme = Portico.Models.Enterprise;
	import pmi = Portico.Models.Interfaces;
	import pml = Portico.Models.Legacy;
	import pms = Portico.Models.Setup;
	import ps = Portico.Services;

	export type KvpMap = { [key: string]: any };

	export interface IDataService {
		// enterprise data
		enterprise: pme.Enterprise;
		// legacy data
		legacy: pml.Legacy;
		// setup data
		setup: pms.Setup;
		// state data
		state: pm.State;
		// local storage
		local: KvpMap;
		// session storage
		session: KvpMap;
		// temporary data
        temp: KvpMap;

        jwtTokenKeyName: string;
        sessionKeyName: string;
        childFlagKeyName: string;

		getDataRows: (table: string, key?: string) => pms.DataRow[];
		getDataRowValue: (dataRow: pms.DataRow, column: string, def?: string) => string;
		getDataTable: (table: string) => pms.DataTable;
		getDataValue: (table: string, column: string, key: string, def?: string) => string;
		getDataValues: (table: string, column: string, key?: string) => string[];
		getEnvironmentId: (key: number | string, def?: number) => number;
		getEnvironmentValue(key: string, def?: string): string;
		getProgramId: () => number;
        getReferenceId: (key: number | string, def?: number) => number;
        //getReferenceValue(key: string): string;
        getReferenceValue(key: string, def?: string): string;
		getTenantId: () => number;
	}

	@Service('app', 'dataService')
	@Inject('serverConstants')
	export class DataService implements IDataService {
		// enterprise data
		enterprise: pme.Enterprise;
		// legacy data
		legacy: pml.Legacy;
		// setup data
		setup: pms.Setup;
		// state data
		state: pm.State;
		// local storage
		local: KvpMap;
		// session storage
		session: KvpMap;
		// permanent data (uses storage)
		perm: KvpMap;
		// temporary data
		temp: KvpMap;

		constructor(private serverConstants: pmi.IServerConstants) {
			this.enterprise = new pme.Enterprise();
			this.legacy = new pml.Legacy();
			this.setup = new pms.Setup();
			this.state = new pm.State();
			this.local = {};
			this.session = {};
			this.perm = this.session;
			this.temp = {};
        }

        public jwtTokenKeyName: string = `${Portico.Models.JWT_STORAGE_KEY}-${this.serverConstants.portalId}`;
        public sessionKeyName: string = `${pm.SESSION_STORAGE_KEY}-${this.serverConstants.portalId}`;
        public childFlagKeyName: string = `${pm.IS_CHILD_KEY}-${this.serverConstants.portalId}`;

		getDataRows(table: string, key: string = null): pms.DataRow[] {
			if (this.setup.portal) {
				const dataTable = this.getDataTable(table);

				if (dataTable) {
					return this.setup.portal.getDataRows(dataTable.id, key);
				}
			}

			return [];
		}

		getDataRowValue(dataRow: pms.DataRow, column: string, def: string = null): string {
			const dataTable = this.getDataTable(dataRow.dataTableId);

			if (dataTable) {
				const dataColumn = dataTable.getDataColumn(column);

				if (dataColumn) {
					return dataRow.getValue(dataColumn.id, def);
				}
			}

			return def;
		}

		getDataTable(table: string): pms.DataTable {
			if (this.setup.dataTables) {
				const dataTables = this.setup.dataTables.filter((dt: pms.DataTable) => {
					return dt.id === table || dt.name === table;
				});

				if (dataTables && dataTables.length === 1) {
					return dataTables[0];
				}
			}

			return null;
		}

		getDataValue(table: string, column: string, key: string, def: string = null): string {
			if (this.setup.portal) {
				const dataTable = this.getDataTable(table);

				if (dataTable) {
					const dataColumn = dataTable.getDataColumn(column);

					if (dataColumn) {
						const dataRows = this.setup.portal.getDataRows(dataTable.id, key);

						if (dataRows && dataRows.length >= 1) {
							const dataRow = dataRows[0];
							const dataValue = dataRow.getDataValue(dataColumn.id);

							if (dataValue) {
								return dataValue.value;
							}
						}
					}
				}
			}

			return def;
		}

		getDataValues(table: string, column: string, key: string = null): string[] {
			const values: string[] = [];

			if (this.setup.portal) {
				const dataTable = this.getDataTable(table);

				if (dataTable) {
					const dataColumn = dataTable.getDataColumn(column);

					if (dataColumn) {
						const dataRows = this.setup.portal.getDataRows(dataTable.id, key);

						if (dataRows) {
							dataRows.forEach((dataRow) => {
								const dataValue = dataRow.getDataValue(dataColumn.id);

								if (dataValue) {
									values.push(dataValue.value);
								}
							});
						}
					}
				}
			}

			return values;
		}

		getEnvironmentId(key: number | string, def: number = null): number {
			if (typeof key === 'string') {
				const environment = this.serverConstants.connectTo;
				const value = this.getDataValue('Environment', environment, key) || this.getDataValue('Reference', 'Id', key) || key;
				return value ? _.toNumber(value) : def;
			}

			return key;
		}

		getEnvironmentValue(key: string, def: string = null): string {
			const environment = this.serverConstants.connectTo;
			const value = this.getDataValue('Environment', environment, key) || this.getDataValue('Reference', 'Id', key);
			return value || def;
		}

		getProgramId(): number {
			return this.getEnvironmentId(this.state.app + '-program') || -1;
		}

		getReferenceId(key: number | string, def: number = null): number {
			if (typeof key === 'string') {
				const value = this.getDataValue('Reference', 'Id', key); // || key;
				return value ? _.toNumber(value) : def;
			}

			return key;
        }

        getReferenceValue(key: string, def: string = null): string {
            return this.getDataValue('Reference', 'Id', key) || def;
        }

		getTenantId(): number {
			return this.getEnvironmentId('tenant') || -1;
		}
    }
}